import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Plus, Loader2, Archive, Save, Building2, X } from "lucide-react";
import { api, formatApiError } from "@/api/client";
import { useAuth } from "@/context/AuthContext";

type Branch = { id: string; name: string; address: string | null; phone: string | null; is_active: boolean };

export default function Branches() {
    const { t } = useTranslation();
    const { hasPerm } = useAuth();
    const [items, setItems] = useState<Branch[]>([]);
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState<string | null>(null);
    const [showNew, setShowNew] = useState(false);
    const [editing, setEditing] = useState<Branch | null>(null);
    const canCreate = hasPerm("branch.create");
    const canEdit = hasPerm("branch.edit");
    const canArchive = hasPerm("branch.archive");

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get<{ items: Branch[] }>("/branches");
            setItems(data.items);
        } catch (e: any) {
            setErr(formatApiError(e));
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => { load(); }, []);

    return (
        <div className="space-y-6" data-testid="branches-page">
            <div className="flex items-center justify-between gap-4 flex-wrap">
                <div>
                    <h1 className="text-3xl font-semibold" style={{ color: "var(--text)" }} data-testid="page-title">{t("branches.title")}</h1>
                    <p className="text-sm mt-1" style={{ color: "var(--text-muted)" }}>{items.length}</p>
                </div>
                {canCreate && (
                    <button data-testid="branches-new-btn" onClick={() => setShowNew(true)} className="inline-flex items-center gap-2 rounded-md px-4 py-2 text-sm font-medium" style={{ background: "var(--primary)", color: "var(--primary-fg)", minHeight: 40 }}>
                        <Plus size={16} /> {t("branches.new")}
                    </button>
                )}
            </div>

            {err && <div className="rounded-md p-3 text-sm" style={{ background: "var(--error-bg)", color: "var(--error-fg)" }}>{err}</div>}

            <div className="surface-card overflow-hidden">
                {loading ? (
                    <div className="p-8 flex justify-center"><Loader2 className="animate-spin" size={22} style={{ color: "var(--text-muted)" }} /></div>
                ) : items.length === 0 ? (
                    <div className="p-8 text-center text-sm" style={{ color: "var(--text-muted)" }} data-testid="branches-empty">
                        <Building2 size={28} className="mx-auto mb-3" />{t("branches.empty")}
                    </div>
                ) : (
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="text-left" style={{ background: "var(--input)", color: "var(--text-muted)" }}>
                                <th className="px-4 py-2 font-medium">{t("common.name")}</th>
                                <th className="px-4 py-2 font-medium">{t("branches.address")}</th>
                                <th className="px-4 py-2 font-medium">{t("branches.phone")}</th>
                                <th className="px-4 py-2 font-medium">{t("common.status")}</th>
                                <th className="px-4 py-2 font-medium text-right">{t("common.actions")}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {items.map((b) => (
                                <tr key={b.id} className="border-t" style={{ borderColor: "var(--border)" }} data-testid={`branch-row-${b.id}`}>
                                    <td className="px-4 py-3" style={{ color: "var(--text)" }}>{b.name}</td>
                                    <td className="px-4 py-3" style={{ color: "var(--text-muted)" }}>{b.address || "—"}</td>
                                    <td className="px-4 py-3" style={{ color: "var(--text-muted)" }}>{b.phone || "—"}</td>
                                    <td className="px-4 py-3">
                                        <span className={`status-pill ${b.is_active ? "status-success" : "status-neutral"}`}>
                                            {b.is_active ? t("common.active") : t("common.archived")}
                                        </span>
                                    </td>
                                    <td className="px-4 py-3 text-right space-x-2">
                                        {canEdit && <button data-testid={`branch-edit-${b.id}`} onClick={() => setEditing(b)} className="text-xs rounded-md px-2 py-1 border" style={{ borderColor: "var(--border)", color: "var(--text)" }}>{t("common.edit")}</button>}
                                        {canArchive && b.is_active && (
                                            <button data-testid={`branch-archive-${b.id}`} onClick={async () => { await api.post(`/branches/${b.id}/archive`); load(); }} className="inline-flex items-center gap-1 text-xs rounded-md px-2 py-1 border" style={{ borderColor: "var(--border)", color: "var(--error-fg)" }}>
                                                <Archive size={12} /> {t("common.archive")}
                                            </button>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>

            {(showNew || editing) && <BranchDialog branch={editing} onClose={() => { setShowNew(false); setEditing(null); }} onSaved={() => { setShowNew(false); setEditing(null); load(); }} />}
        </div>
    );
}

function BranchDialog({ branch, onClose, onSaved }: { branch: Branch | null; onClose: () => void; onSaved: () => void }) {
    const { t } = useTranslation();
    const [name, setName] = useState(branch?.name || "");
    const [address, setAddress] = useState(branch?.address || "");
    const [phone, setPhone] = useState(branch?.phone || "");
    const [loading, setLoading] = useState(false);
    const [err, setErr] = useState<string | null>(null);

    const submit = async () => {
        setLoading(true); setErr(null);
        try {
            if (branch) await api.patch(`/branches/${branch.id}`, { name, address, phone });
            else await api.post("/branches", { name, address, phone });
            onSaved();
        } catch (e: any) { setErr(formatApiError(e)); } finally { setLoading(false); }
    };

    return (
        <div className="fixed inset-0 z-40 flex items-center justify-center p-4" style={{ background: "rgba(0,0,0,0.5)" }} data-testid="branch-dialog" onClick={onClose}>
            <div className="surface-card w-full max-w-md p-6" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-center justify-between mb-4">
                    <h2 className="text-lg font-semibold" style={{ color: "var(--text)" }}>{branch ? t("common.edit") : t("branches.new")}</h2>
                    <button data-testid="branch-dialog-close" onClick={onClose} style={{ color: "var(--text-muted)" }}><X size={18} /></button>
                </div>
                <div className="space-y-3">
                    <Field label={t("common.name")}><input data-testid="branch-name-input" value={name} onChange={(e) => setName(e.target.value)} className="surface-input px-3 py-2 w-full text-sm" style={{ minHeight: 44 }} /></Field>
                    <Field label={t("branches.address")}><input data-testid="branch-address-input" value={address} onChange={(e) => setAddress(e.target.value)} className="surface-input px-3 py-2 w-full text-sm" style={{ minHeight: 44 }} /></Field>
                    <Field label={t("branches.phone")}><input data-testid="branch-phone-input" value={phone} onChange={(e) => setPhone(e.target.value)} className="surface-input px-3 py-2 w-full text-sm" style={{ minHeight: 44 }} /></Field>
                    {err && <div className="text-sm" style={{ color: "var(--error-fg)" }}>{err}</div>}
                    <button data-testid="branch-save-btn" disabled={loading || !name.trim()} onClick={submit} className="w-full inline-flex justify-center items-center gap-2 rounded-md py-2 text-sm font-medium disabled:opacity-60" style={{ background: "var(--primary)", color: "var(--primary-fg)", minHeight: 44 }}>
                        {loading ? <Loader2 className="animate-spin" size={16} /> : <Save size={16} />} {t("common.save")}
                    </button>
                </div>
            </div>
        </div>
    );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
    return <label className="block"><div className="text-xs mb-1" style={{ color: "var(--text-muted)" }}>{label}</div>{children}</label>;
}
